import {
  ContentAudience,
  ContentWorkflow,
  PublisherReviewStatus,
  TechWriterStatus,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { useWorkflow } from '../v2-hooks/useWorkflow';

export type StageState = 'completed' | 'in-progress' | 'rejected' | 'pending';

export interface ProgressStage {
  readonly label: string;
  readonly state: StageState;
}

export interface WorkflowProgressBarViewModel {
  /** Ordered stages with their resolved visual state. */
  readonly stages: readonly ProgressStage[];
  /** Summary label shown below the bubbles. */
  readonly stageLabel: string;
}

interface StageDefinition {
  readonly label: string;
  readonly key: string;
}

const INTERNAL_STAGES: readonly StageDefinition[] = [
  { label: 'Draft', key: 'draft' },
  { label: 'Quality check', key: 'quality' },
  { label: 'Publisher review', key: 'publisher' },
  { label: 'Published', key: 'published' },
];

const EXTERNAL_STAGES: readonly StageDefinition[] = [
  { label: 'Draft', key: 'draft' },
  { label: 'Quality check', key: 'quality' },
  { label: 'Publisher review', key: 'publisher' },
  { label: 'Selection pool', key: 'selection' },
  { label: 'Tech writer review', key: 'techwriter' },
  { label: 'Published', key: 'published' },
];

interface MappedStage {
  readonly key: string;
  readonly subLabel?: string;
  readonly isRejected: boolean;
}

const selectionPoolSentBackStatuses = new Set<TechWriterStatus>([
  TechWriterStatus.SentBackToAuthor,
  TechWriterStatus.CheckAuthorAvailability,
  TechWriterStatus.AuthorNotResponding,
]);

function mapWorkflowToStage(workflow: ContentWorkflow): MappedStage {
  const { draftState, publisherReviewInfo, techWriterStatus } = workflow;

  switch (draftState) {
    case WorkflowState.NEW:
    case WorkflowState.UNMODIFIED:
    case WorkflowState.MODIFIED:
      return { key: 'draft', subLabel: 'In progress', isRejected: false };

    case WorkflowState.QUALITY_CHECK:
      return { key: 'quality', subLabel: 'In progress', isRejected: false };

    case WorkflowState.PUBLISHER_REVIEW:
      if (publisherReviewInfo?.reviewStatus === PublisherReviewStatus.REJECTED) {
        return { key: 'publisher', subLabel: 'Rejected', isRejected: true };
      }
      return { key: 'publisher', subLabel: 'In progress', isRejected: false };

    case WorkflowState.PUBLISHED:
      if (workflow.contentAudience === ContentAudience.INTERNAL) {
        return { key: 'published', isRejected: false };
      }
      // @ts-expect-error TS2345 - strict migration
      if (selectionPoolSentBackStatuses.has(techWriterStatus)) {
        return { key: 'selection', subLabel: 'Sent back', isRejected: true };
      }
      return { key: 'selection', subLabel: 'Evaluation', isRejected: false };

    case WorkflowState.FINALIZING:
      if (techWriterStatus === TechWriterStatus.Complete) {
        return { key: 'published', subLabel: 'In process', isRejected: false };
      }
      return { key: 'techwriter', subLabel: 'In progress', isRejected: false };

    case WorkflowState.FINALIZED:
    case WorkflowState.DISTRIBUTED:
      return { key: 'published', isRejected: false };

    case WorkflowState.REVOKED:
      return { key: 'published', subLabel: 'Removed from external site', isRejected: true };

    case WorkflowState.UNPUBLISHED:
      return { key: 'draft', subLabel: 'Unpublished', isRejected: true };

    default: {
      return { key: 'draft', subLabel: 'In progress', isRejected: false };
    }
  }
}

function resolveStageState(
  stepIndex: number,
  activeIndex: number,
  isRejected: boolean,
  isTerminal: boolean,
): StageState {
  if (isTerminal) return 'completed';
  if (stepIndex < activeIndex) return 'completed';
  if (stepIndex === activeIndex) return isRejected ? 'rejected' : 'in-progress';
  return 'pending';
}

function buildStages(stages: readonly StageDefinition[], mapped: MappedStage): ProgressStage[] {
  const activeIndex = stages.findIndex((s) => s.key === mapped.key);
  const resolvedIndex = activeIndex === -1 ? 0 : activeIndex;
  const isTerminal = mapped.key === 'published' && !mapped.isRejected && !mapped.subLabel;

  return stages.map((def, index) => ({
    label: def.label,
    state: resolveStageState(index, resolvedIndex, mapped.isRejected, isTerminal),
  }));
}

function buildStageLabel(stages: readonly StageDefinition[], mapped: MappedStage): string {
  const stage = stages.find((s) => s.key === mapped.key);
  const name = stage?.label ?? 'Draft';

  if (mapped.key === 'published' && !mapped.isRejected && !mapped.subLabel) return 'Published';

  if (mapped.key === 'published' && mapped.subLabel === 'In process') {
    return 'Publishing: In process';
  }

  if (mapped.subLabel === 'Unpublished') return 'Unpublished';
  if (mapped.subLabel === 'Removed from external site') return 'Removed from external site';

  if (!mapped.subLabel) return name;
  return `${name}: ${mapped.subLabel}`;
}

export function useWorkflowProgressBarViewModel(): WorkflowProgressBarViewModel {
  const workflow = useWorkflow();

  if (!workflow) {
    return {
      stages: INTERNAL_STAGES.map((def, i) => ({
        label: def.label,
        state: (i === 0 ? 'in-progress' : 'pending') as StageState,
      })),
      stageLabel: 'Draft: In progress',
    };
  }

  const mapped = mapWorkflowToStage(workflow);
  const stages =
    workflow.contentAudience !== ContentAudience.INTERNAL ? EXTERNAL_STAGES : INTERNAL_STAGES;

  return {
    stages: buildStages(stages, mapped),
    stageLabel: buildStageLabel(stages, mapped),
  };
}
